import React from 'react';
import Button from './Button';

interface AboutPageProps {
  onBack: () => void;
}

const AboutPage: React.FC<AboutPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center text-primary-600 hover:text-primary-700 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-midnight">About AI Marker Mate</h1>
          <p className="text-gray-600 mt-2">Empowering educators through intelligent assessment technology</p>
        </div>

        <div className="space-y-8">
          {/* Company Story */}
          <div className="bg-white rounded-xl shadow-soft p-8">
            <div className="flex items-center mb-6">
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/transparent%20logo%20market%20mate%20(2).webp?alt=media&token=577c40fb-e028-4260-b65d-3545512569dd" 
                alt="AI Marker Mate Logo" 
                className="h-12 w-auto mr-4"
              />
              <h2 className="text-2xl font-semibold text-midnight">Our Story</h2>
            </div>
            
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
              <p className="mb-6">
                AI Marker Mate is a proudly <strong>Australian company</strong> founded by a team of AI experts who 
                recognized the transformative potential of generative artificial intelligence in education. 
                Our journey began with a deeply personal inspiration: one of our founder's wife, a dedicated 
                history teacher, who spent countless hours providing meaningful feedback to her students' essays.
              </p>
              
              <p className="mb-6">
                Watching her dedication and the challenges she faced in balancing quality feedback with time 
                constraints, we saw an opportunity to harness the power of AI to support educators rather than 
                replace them. This personal connection to the teaching profession drives everything we do.
              </p>
              
              <p className="mb-6">
                We specialize in the <strong>Australian history curriculum</strong>, understanding the unique 
                requirements, assessment standards, and pedagogical approaches that define excellence in 
                Australian historical education. Our platform is built by educators, for educators, with a 
                deep appreciation for the nuanced skills required in history teaching and assessment.
              </p>
            </div>
          </div>

          {/* Our Mission */}
          <div className="bg-primary rounded-xl shadow-soft p-8 text-white">
            <h2 className="text-2xl font-semibold mb-6">Our Mission</h2>
            <div className="text-white/95 leading-relaxed">
              <p className="text-lg mb-6">
                We believe that AI should <strong>enhance and amplify</strong> the incredible work that teachers 
                already do, not replace the human element that makes education meaningful. Our mission is to 
                provide teachers with intelligent tools that help them deliver higher quality feedback more 
                efficiently, ultimately benefiting both educators and students.
              </p>
              
              <p className="text-lg">
                By leveraging generative AI, we enable teachers to focus on what they do best: inspiring students, 
                facilitating critical thinking, and providing the personalized guidance that only human educators 
                can offer.
              </p>
            </div>
          </div>

          {/* Key Benefits */}
          <div className="bg-white rounded-xl shadow-soft p-8">
            <h2 className="text-2xl font-semibold text-midnight mb-6">How We Help Educators</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-midnight mb-2">Faster Marking</h3>
                    <p className="text-gray-600">
                      Reduce the time spent on initial assessment while maintaining thorough, consistent evaluation standards.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-midnight mb-2">More Effective Marking</h3>
                    <p className="text-gray-600">
                      AI-powered analysis helps identify key strengths and areas for improvement with consistency and depth.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-1l-4 4z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-midnight mb-2">Quality Feedback</h3>
                    <p className="text-gray-600">
                      Generate comprehensive, constructive feedback that helps students understand their progress and next steps.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-midnight mb-2">Scale Your Impact</h3>
                    <p className="text-gray-600">
                      Help more students with the same time investment, ensuring no student falls through the cracks.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Important Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-amber-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-lg font-semibold text-amber-800 mb-2">AI as Enhancement, Not Replacement</h3>
                <p className="text-amber-700">
                  AI Marker Mate does not replace the marking and feedback that teachers provide. Instead, 
                  our platform aims to boost, enhance, and speed up the assessment process so that students 
                  can receive feedback faster and make improvements more quickly. The teacher's expertise, 
                  judgment, and personal connection remain at the heart of quality education.
                </p>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="bg-white rounded-xl shadow-soft p-8 text-center">
            <h2 className="text-2xl font-semibold text-midnight mb-4">Ready to Transform Your Assessment Process?</h2>
            <p className="text-gray-600 mb-6">
              Join the growing community of Australian educators who are using AI to enhance their teaching effectiveness.
            </p>
            <Button 
              onClick={onBack}
              variant="primary"
              size="lg"
            >
              Get Started Today
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
